// GLobals
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

// Store
import { useAppDispatch, useAppState } from '@/store';
import {
  changeProductOfMonthQuery,
  deleteProductsOfMonth,
  getProductsOfMonthList,
  productsSelector,
} from '../../feature';

// Components
import {
  StyledHeaderBlock,
  StyledPageWrapper,
  StyledPageTitle,
  StyledContentBlock,
  StyledTitleModal,
  StyledButtonsWrapper,
  StyledTabsWrap,
  StyledCreateButton,
  StyledDescription,
} from './ProductOfMonthContainerStyles';
import { Button, Drawer } from '@/ui';
import { PlusCircleIcon } from '@/icons';
import { Modal, ModalSize, showSuccessToast, useForm } from '@/components';
import { ProductsOfMonthList } from '../../components';
import CreateProductOfMonthContainer from '../CreateProductOfMonthContainer/CreateProductOfMonthContainer';

// Models
import { IProduct } from '@/models/products.model';

// Modules
import { Resource, usePermissions } from '@/modules/Permissions';

// Helpers
import { GetProductsOfMonthListDTO } from '../../dtos';
import { useModal, usePagination } from '@/hooks';

const ProductOfMonthContainer = () => {
  const { t } = useTranslation(['products']);
  const dispatch = useAppDispatch();
  const [form] = useForm();
  const [currentProduct, setCurrentProduct] = useState<IProduct | null>(null);
  const { isOpened, openModal, closeModal } = useModal();
  const { isOpened: productFormOpened, openModal: openProductForm, closeModal: closeProductForm } = useModal();
  const { isLoading, productsOfMonthList, productsOfMonthQuery } = useAppState(productsSelector);
  const { currentPage, onPageChange } = usePagination(productsOfMonthList?.meta?.current_page);
  const { canManage } = usePermissions();
  const canManageProductOfMonth = canManage(Resource.PRODUCT_OF_MONTH);

  const getProductsListHandler = () => {
    const dto = new GetProductsOfMonthListDTO({ ...productsOfMonthQuery, page: currentPage });
    dispatch(getProductsOfMonthList(dto));
  };

  const changeProductForDelete = (product: IProduct) => {
    setCurrentProduct(product);
    openModal();
  };

  const handleEditClick = async (product: IProduct) => {
    await form.resetFields();
    setCurrentProduct(product);
    openProductForm();
  };

  const handleDelete = async () => {
    try {
      if (currentProduct) await dispatch(deleteProductsOfMonth(currentProduct.id));
      getProductsListHandler();
      showSuccessToast({ message: t('product_deleted') });
      closeModal();
      setCurrentProduct(null);
    } catch {}
  };

  const handleCloseModal = () => {
    closeModal();
    setCurrentProduct(null);
  };

  const handleCloseDrawer = () => {
    closeProductForm();
    setCurrentProduct(null);
    form.resetFields();
  };

  useEffect(() => {
    getProductsListHandler();
  }, [currentPage]);

  useEffect(() => {
    return () => {
      dispatch(changeProductOfMonthQuery({}));
    };
  }, []);

  return (
    <StyledPageWrapper>
      <StyledHeaderBlock>
        <StyledPageTitle>{t('products_of_month_tab_title')}</StyledPageTitle>
        {canManageProductOfMonth && (
          <StyledTabsWrap>
            <StyledCreateButton onClick={openProductForm}>
              <PlusCircleIcon />
              {t('products_of_month_tab_add_button')}
            </StyledCreateButton>
          </StyledTabsWrap>
        )}
      </StyledHeaderBlock>

      <StyledContentBlock>
        <ProductsOfMonthList
          productsList={productsOfMonthList?.data as IProduct[]}
          currentPage={currentPage}
          onPageChange={onPageChange}
          handleEditClick={handleEditClick}
          setProductForDelete={changeProductForDelete}
          total={productsOfMonthList?.meta?.total ?? 0}
          isLoading={isLoading}
        />
      </StyledContentBlock>
      <Drawer
        open={productFormOpened}
        onClose={handleCloseDrawer}
        width="29rem"
        destroyOnClose
        title={t('products_of_month_tab_add_button')}
      >
        <CreateProductOfMonthContainer
          currentProduct={currentProduct}
          handleCloseDrawer={handleCloseDrawer}
          formInstance={form}
          isLoading={isLoading}
        />
      </Drawer>
      <Modal visible={isOpened} onCancel={handleCloseModal} size={ModalSize.LARGE}>
        <StyledTitleModal>{t('products_delete_title')}</StyledTitleModal>
        <StyledDescription>{t('products_delete_description')}</StyledDescription>
        <StyledButtonsWrapper>
          <Button onClick={handleCloseModal} type="primary">
            {t('products_delete_back')}
          </Button>
          <Button onClick={handleDelete} type="text">
            {t('products_delete')}
          </Button>
        </StyledButtonsWrapper>
      </Modal>
    </StyledPageWrapper>
  );
};

export default ProductOfMonthContainer;
